import {
  OPTION_STATES,
  ORDER_SIDES,
  ORDER_STATES,
  ORDER_TYPES,
  OUTCOME_TYPES,
  USER_ACTIVITY_TYPES,
  USER_POSITION_STATES
} from './predictionMarket.constants'
import { getTranslation } from 'helpers/translations.helpers'
import { divide, getPrecision, minus, plus, times } from 'utils/math.utils'
import { getOptionChance } from './predictionMarket.helpers'
import {
  demoOrdersAdd,
  demoOrdersRemove,
  demoOrdersReset,
  demoMarketClosed,
  demoMarketSettled,
  demoSyncBook,
  marketActivityReceived,
  setDemoWallet,
  userActivityReceived,
  userPositionUpdated,
  walletBalanceNotificationReceived
} from './slice-thunk/predictionMarket.slice'

export const DEMO_WALLET_BALANCE = 1000

export const DEMO_QUESTION_ID = 0
export const DEMO_OPTION_ID = 1
export const DEMO_OPTION_IDS = [1, 2, 3]

export const isValidDemoOptionId = (optionId) =>
  DEMO_OPTION_IDS.includes(+optionId)

const getDemoOptionMeta = (optionId) =>
  DEMO_MARKET_DATA.market.options.find((option) => +option.id === +optionId)

export const isDemoModeFromSearch = (search = '') => {
  const params = new URLSearchParams(search)
  return params.get('demo') === 'true' || params.get('isDemo') === 'true'
}

export const isDemoMarketId = (marketId) =>
  marketId != null && +marketId === DEMO_QUESTION_ID

const now = Date.now()
const hourMs = 60 * 60 * 1000

const buildGraphData = (basePrice = 48) => {
  const data = Array.from({ length: 24 }, (_, index) => ({
    timestamp: now - (23 - index) * hourMs,
    price: basePrice + Math.sin(index / 3) * 4
  }))

  return [{ interval: 'ALL', data }]
}

export const DEMO_MARKET_DATA = {
  questionId: DEMO_QUESTION_ID,
  categories: ['Politics', 'Sports', 'Crypto', 'Tutorial'],
  market: {
    id: DEMO_QUESTION_ID,
    title: 'Demo multi-outcome market',
    category: 'Tutorial',
    thumbnailUrl: null,
    takerFeeCoefficient: 20,
    makerRebatePercent: 0,
    volumes: 12500,
    options: [
      {
        id: 1,
        title: 'Outcome Alpha',
        thumbnailUrl: null,
        state: 'open',
        outcome: null,
        yesPrice: 48,
        noPrice: 52,
        volumes: 4200,
        optionDataPoints: buildGraphData(48)
      },
      {
        id: 2,
        title: 'Outcome Beta',
        thumbnailUrl: null,
        state: 'open',
        outcome: null,
        yesPrice: 62,
        noPrice: 38,
        volumes: 3800,
        optionDataPoints: buildGraphData(62)
      },
      {
        id: 3,
        title: 'Outcome Gamma',
        thumbnailUrl: null,
        state: 'open',
        outcome: null,
        yesPrice: 35,
        noPrice: 65,
        volumes: 4500,
        optionDataPoints: buildGraphData(35)
      }
    ]
  },
  comments: [
    {
      id: 1,
      userId: 999,
      userCode: 'poly-trader',
      thumbnailUrl: null,
      comment: 'Great market to learn limit orders — try placing a bid below the current price.',
      parentCommentId: null,
      likes: 3,
      likedByMe: false,
      createdAt: new Date(now - 2 * hourMs).toISOString(),
      replies: [],
      repliesCount: 0
    },
    {
      id: 2,
      userId: 998,
      userCode: 'demo-learner',
      thumbnailUrl: null,
      comment: 'The orderbook updates live once you start placing demo orders.',
      parentCommentId: null,
      likes: 1,
      likedByMe: false,
      createdAt: new Date(now - hourMs).toISOString(),
      replies: [],
      repliesCount: 0
    }
  ],
  marketActivity: [
    {
      id: 1,
      type: USER_ACTIVITY_TYPES.BOUGHT_SHARES,
      questionId: DEMO_QUESTION_ID,
      optionId: DEMO_OPTION_ID,
      createdAt: new Date(now - hourMs).toISOString(),
      log: {
        trade: {
          outcome: OUTCOME_TYPES.YES,
          price: 48,
          quantity: 10,
          buyerName: 'Demo Trader',
          sellerName: 'SYSTEM'
        }
      }
    },
    {
      id: 2,
      type: USER_ACTIVITY_TYPES.SOLD_SHARES,
      questionId: DEMO_QUESTION_ID,
      optionId: DEMO_OPTION_ID,
      createdAt: new Date(now - 2 * hourMs).toISOString(),
      log: {
        trade: {
          outcome: OUTCOME_TYPES.NO,
          price: 52,
          quantity: 5,
          buyerName: 'SYSTEM',
          sellerName: 'Market Maker'
        }
      }
    }
  ],
  orderbooksByOptionId: {
    1: {
      yes: { bid: { 45: 80, 48: 120, 42: 60 }, ask: { 52: 100, 55: 60, 58: 40 } },
      no: { bid: { 45: 70, 48: 90, 42: 50 }, ask: { 52: 85, 55: 50, 58: 35 } }
    },
    2: {
      yes: { bid: { 59: 70, 62: 110, 56: 55 }, ask: { 66: 90, 69: 55, 72: 35 } },
      no: { bid: { 31: 60, 34: 85, 28: 45 }, ask: { 38: 75, 41: 45, 44: 30 } }
    },
    3: {
      yes: { bid: { 32: 90, 35: 130, 29: 65 }, ask: { 39: 95, 42: 58, 45: 38 } },
      no: { bid: { 58: 75, 61: 100, 55: 52 }, ask: { 65: 88, 68: 52, 71: 32 } }
    }
  },
  topHolders: {
    yes: [
      {
        thumbnailUrl: null,
        userCode: 'alpha-trader',
        firstName: 'Alex',
        lastName: 'Rivera',
        shares: 240,
        userId: 11
      },
      {
        thumbnailUrl: null,
        userCode: 'market-maker',
        firstName: 'Morgan',
        lastName: 'Lee',
        shares: 210,
        userId: 12
      }
    ],
    no: [
      {
        thumbnailUrl: null,
        userCode: 'hedge-bot',
        firstName: 'Sam',
        lastName: 'Chen',
        shares: 180,
        userId: 13
      }
    ]
  },
  topPositions: [
    {
      thumbnailUrl: null,
      userCode: 'alpha-trader',
      firstName: 'Alex',
      lastName: 'Rivera',
      yesShares: 240,
      noShares: 60,
      realizedPnl: 18.4,
      userId: 11
    }
  ],
  positionsByOptionId: {
    1: { optionId: 1, yesShares: 25, noShares: 10, lockedYesShares: 0, lockedNoShares: 0, avgYesPrice: 48, avgNoPrice: 52, realizedPnl: 4.5 },
    2: { optionId: 2, yesShares: 0, noShares: 0, lockedYesShares: 0, lockedNoShares: 0, avgYesPrice: 0, avgNoPrice: 0, realizedPnl: 0 },
    3: { optionId: 3, yesShares: 15, noShares: 5, lockedYesShares: 0, lockedNoShares: 0, avgYesPrice: 35, avgNoPrice: 65, realizedPnl: 2.1 }
  },
  orders: []
}

const emptyDemoPosition = (optionId) => ({
  optionId: +optionId,
  yesShares: 0,
  noShares: 0,
  lockedYesShares: 0,
  lockedNoShares: 0,
  avgYesPrice: 0,
  avgNoPrice: 0,
  realizedPnl: 0
})

const updateAvgBuyPrice = (currentAvg, currentShares, fillShares, fillPrice) => {
  if (+fillShares <= 0) return +(currentAvg ?? 0)
  const nextShares = plus(currentShares, fillShares)
  if (+nextShares <= 0) return +fillPrice
  return getPrecision(divide(
    plus(times(+(currentAvg ?? 0), currentShares), times(fillPrice, fillShares)),
    nextShares
  ))
}

const applySellRealizedPnl = (position, outcome, qty, fillPrice) => {
  const avg = outcome === OUTCOME_TYPES.YES ? (position.avgYesPrice ?? 0) : (position.avgNoPrice ?? 0)
  return getPrecision(plus(position.realizedPnl ?? 0, divide(times(qty, minus(fillPrice, avg)), 100)))
}

const computeResolutionProfit = (position, outcome) => {
  const winningShares = outcome === OUTCOME_TYPES.YES ? position.yesShares : position.noShares
  const losingShares = outcome === OUTCOME_TYPES.YES ? position.noShares : position.yesShares
  const avgWin = outcome === OUTCOME_TYPES.YES ? (position.avgYesPrice ?? 0) : (position.avgNoPrice ?? 0)
  const avgLoss = outcome === OUTCOME_TYPES.YES ? (position.avgNoPrice ?? 0) : (position.avgYesPrice ?? 0)
  let profit = times(winningShares, divide(minus(100, avgWin), 100))
  profit = plus(profit, times(losingShares, divide(minus(0, avgLoss), 100)))
  return getPrecision(profit)
}

const cloneDemoData = () => ({
  comments: DEMO_MARKET_DATA.comments.map((comment) => ({
    ...comment,
    replies: [...(comment.replies ?? [])]
  })),
  repliesByParent: {},
  marketActivity: [...DEMO_MARKET_DATA.marketActivity],
  positionsByOptionId: Object.fromEntries(
    Object.entries(DEMO_MARKET_DATA.positionsByOptionId).map(([id, pos]) => [id, { ...pos }])
  ),
  orders: [],
  walletBalance: DEMO_WALLET_BALANCE,
  nextCommentId: 100,
  nextActivityId: 100,
  marketClosed: false,
  marketSettled: false,
  resolvedOutcomesByOptionId: {}
})

let demoState = cloneDemoData()

const DEMO_SYSTEM_USER_ID = 999
const DEMO_USER_ID = 1
const MAX_DEMO_MATCH_ITERATIONS = 20
const MIN_MARKET_REMAINING = 0.01

const isDemoOrderOpen = (order) => {
  if (![ORDER_STATES.OPEN, ORDER_STATES.PARTIALLY_FILLED].includes(order.state)) return false
  if (order.orderType === ORDER_TYPES.MARKET && order.side === ORDER_SIDES.BID) {
    return +order.remainingAmount > MIN_MARKET_REMAINING
  }
  if (order.orderType === ORDER_TYPES.MARKET && order.side === ORDER_SIDES.ASK) {
    return +order.remainingShares > MIN_MARKET_REMAINING
  }
  return +order.remainingShares > 0
}

const sortLimits = (orders, side, outcome, optionId) => orders
  .filter((order) => order.orderType === ORDER_TYPES.LIMIT && order.side === side && order.outcome === outcome && +order.optionId === +optionId)
  .sort((a, b) => (side === ORDER_SIDES.BID ? +b.price - +a.price : +a.price - +b.price) || +a.id - +b.id)

const spendPerShare = (priceCents) => {
  const p = +priceCents
  const coeff = DEMO_MARKET_DATA.market.takerFeeCoefficient ?? 0
  return p / 100 + (p * (100 - p) * coeff) / 1e6
}

const canMarketOrderMatch = (market, openOrders) => {
  if (market.orderType !== ORDER_TYPES.MARKET) return false
  const optionId = market.optionId

  if (market.side === ORDER_SIDES.BID) {
    if (+market.remainingAmount <= MIN_MARKET_REMAINING) return false
    const ask = sortLimits(openOrders, ORDER_SIDES.ASK, market.outcome, optionId)[0]
    const oppBid = sortLimits(
      openOrders,
      ORDER_SIDES.BID,
      market.outcome === OUTCOME_TYPES.YES ? OUTCOME_TYPES.NO : OUTCOME_TYPES.YES,
      optionId
    )[0]
    const askPrice = ask && +ask.userId !== +market.userId ? +ask.price : Infinity
    const mintPrice = oppBid && +oppBid.userId !== +market.userId ? 100 - +oppBid.price : Infinity
    const bestPrice = Math.min(askPrice, mintPrice)
    if (!Number.isFinite(bestPrice)) return false
    return +market.remainingAmount >= spendPerShare(bestPrice)
  }

  if (+market.remainingShares <= MIN_MARKET_REMAINING) return false
  const bid = sortLimits(openOrders, ORDER_SIDES.BID, market.outcome, optionId)[0]
  return Boolean(bid && +bid.userId !== +market.userId)
}

const emitDemoOrderClosed = (dispatch, order) => {
  dispatch(userActivityReceived({
    type: USER_ACTIVITY_TYPES.ORDER_CLOSED,
    log: { order },
    questionId: DEMO_QUESTION_ID,
    optionId: order.optionId ?? DEMO_OPTION_ID,
    userId: DEMO_USER_ID,
    myUserId: DEMO_USER_ID
  }))
}

const closeExhaustedMarketOrders = ({ dispatch, getState }) => {
  let orders = (getState().predictionMarket.demoOrders ?? []).map((order) => ({ ...order }))
  let walletRefund = 0
  let changed = false

  orders = orders.map((order) => {
    if (order.orderType !== ORDER_TYPES.MARKET) return order
    if (![ORDER_STATES.OPEN, ORDER_STATES.PARTIALLY_FILLED].includes(order.state)) return order

    const openPeers = orders.filter((peer) => +peer.id !== +order.id && isDemoOrderOpen(peer))
    const dustDone = order.side === ORDER_SIDES.BID
      ? +order.remainingAmount <= MIN_MARKET_REMAINING
      : +order.remainingShares <= MIN_MARKET_REMAINING
    const exhausted = dustDone || !canMarketOrderMatch(order, [...openPeers, order])

    if (!exhausted) return order

    if (order.side === ORDER_SIDES.BID && +order.remainingAmount > 0) {
      walletRefund += +order.remainingAmount
    }

    changed = true
    const closed = {
      ...order,
      state: ORDER_STATES.FILLED,
      remainingAmount: 0,
      remainingShares: 0
    }
    emitDemoOrderClosed(dispatch, closed)
    return closed
  })

  if (!changed) return

  demoState.orders = orders.filter((order) => order.userId === DEMO_USER_ID)
  if (walletRefund) {
    demoState.walletBalance += walletRefund
    dispatch(setDemoWallet(demoGetWallet()))
  }
  dispatch(demoOrdersReset(orders))
  syncDemoBookToStore(dispatch, orders, getState)
}

const getDemoPosition = (optionId, getState) => {
  const optId = +optionId
  const chosenId = getState?.()?.predictionMarket?.chosenMarket?.chosenOptionId
  if (chosenId != null && +chosenId === optId) {
    const reduxPos = getState().predictionMarket.myOptionPosition
    if (reduxPos && +reduxPos.optionId === optId) return { ...reduxPos }
  }
  return { ...(demoState.positionsByOptionId[optId] ?? emptyDemoPosition(optId)) }
}

const setDemoPosition = (optionId, position) => {
  demoState.positionsByOptionId[+optionId] = { ...position, optionId: +optionId }
}

const emptyDemoBook = () => ({
  yes: { bid: {}, ask: {} },
  no: { bid: {}, ask: {} }
})

/** Aggregate open limit orders into a display orderbook (single source of truth). */
const buildDemoOrderbook = (optionId, orders = []) => {
  const book = emptyDemoBook()
  for (const order of orders) {
    if (+order.optionId !== +optionId) continue
    if (order.orderType !== ORDER_TYPES.LIMIT) continue
    if (!isDemoOrderOpen(order)) continue
    const shares = +order.remainingShares || 0
    if (shares <= 0) continue
    const map = book[order.outcome]?.[order.side]
    if (!map) continue
    map[order.price] = (map[order.price] ?? 0) + shares
  }
  return book
}

const recordDemoMarketActivity = (payload) => {
  demoState.marketActivity.unshift({
    id: demoState.nextActivityId++,
    type: payload.type,
    questionId: payload.questionId ?? DEMO_QUESTION_ID,
    optionId: payload.optionId ?? DEMO_OPTION_ID,
    createdAt: payload.createdAt ?? new Date().toISOString(),
    log: payload.log
  })
}

const oppositeOutcome = (outcome) =>
  outcome === OUTCOME_TYPES.YES ? OUTCOME_TYPES.NO : OUTCOME_TYPES.YES

const wouldLimitCross = (order, openOrders) => {
  if (order.orderType !== ORDER_TYPES.LIMIT) return false
  const optionId = +order.optionId
  const peers = openOrders.filter((peer) => +peer.id !== +order.id && isDemoOrderOpen(peer))

  if (order.side === ORDER_SIDES.BID) {
    const ask = sortLimits(peers, ORDER_SIDES.ASK, order.outcome, optionId)
      .find((peer) => +peer.userId !== +order.userId)
    if (ask && +ask.price <= +order.price) return true
    const oppBid = sortLimits(peers, ORDER_SIDES.BID, oppositeOutcome(order.outcome), optionId)
      .find((peer) => +peer.userId !== +order.userId)
    if (oppBid && +order.price + +oppBid.price >= 100) return true
    return false
  }

  const bid = sortLimits(peers, ORDER_SIDES.BID, order.outcome, optionId)
    .find((peer) => +peer.userId !== +order.userId)
  return Boolean(bid && +bid.price >= +order.price)
}

const syncDemoBookToStore = (dispatch, orders, getState) => {
  const optionId = getState?.()?.predictionMarket?.chosenMarket?.chosenOptionId ?? DEMO_OPTION_ID
  const orderbook = buildDemoOrderbook(optionId, orders)
  const defaultChance = getDemoOptionMeta(optionId)?.yesPrice ?? 48
  dispatch(demoSyncBook({
    optionId,
    yesPrice: getOptionChance(orderbook, defaultChance),
    orderbook,
    myLimitOrders: orders
      .filter((order) => +order.userId === DEMO_USER_ID && +order.optionId === +optionId && order.orderType === ORDER_TYPES.LIMIT && isDemoOrderOpen(order))
      .map((order) => ({ ...order, shares: +(order.remainingShares ?? order.shares) }))
  }))
}

const syncDemoPosition = (dispatch, position) => {
  if (!position) return
  dispatch(userPositionUpdated({ ...position }))
}

const bookToDemoOrders = (orderbook, optionId, startId = 2000) => {
  const orders = []
  let id = startId
  const optionMeta = getDemoOptionMeta(optionId)
  for (const outcome of [OUTCOME_TYPES.YES, OUTCOME_TYPES.NO]) {
    for (const side of [ORDER_SIDES.BID, ORDER_SIDES.ASK]) {
      for (const [price, shares] of Object.entries(orderbook?.[outcome]?.[side] ?? {})) {
        if (+price <= 0 || +shares <= 0) continue
        orders.push({
          id: id++,
          userId: DEMO_SYSTEM_USER_ID,
          questionId: DEMO_QUESTION_ID,
          optionId: +optionId,
          orderType: ORDER_TYPES.LIMIT,
          side,
          outcome,
          price: +price,
          shares: +shares,
          remainingShares: +shares,
          remainingAmount: 0,
          amount: 0,
          state: ORDER_STATES.OPEN,
          questionTitle: DEMO_MARKET_DATA.market.title,
          optionTitle: optionMeta?.title ?? getTranslation('demoOutcome')
        })
      }
    }
  }
  return orders
}

export const getDemoSeedOrders = () =>
  DEMO_OPTION_IDS.flatMap((optionId) =>
    bookToDemoOrders(DEMO_MARKET_DATA.orderbooksByOptionId[optionId], optionId, 2000 + optionId * 1000)
  )

export const resetDemoState = () => {
  demoState = cloneDemoData()
}

const paginate = (rows, { page = 1, limit = 20 } = {}) => {
  const start = (page - 1) * limit
  return {
    rows: rows.slice(start, start + limit),
    count: rows.length
  }
}

const getMyLimitOrders = (params = {}) => {
  let rows = [...demoState.orders]

  if (params.optionId != null) {
    rows = rows.filter((order) => +order.optionId === +params.optionId)
  }
  if (params.orderType) {
    rows = rows.filter((order) => order.orderType === params.orderType)
  }
  if (params.state) {
    rows = rows.filter((order) => {
      if (params.state === ORDER_STATES.OPEN) {
        return order.state === ORDER_STATES.OPEN || order.state === ORDER_STATES.PARTIALLY_FILLED
      }
      return order.state === params.state
    })
  }
  if (params.side) {
    rows = rows.filter((order) => order.side === params.side)
  }

  return rows.filter((order) => order.userId === 1)
}

export const demoGetWallet = () => ({ balance: demoState.walletBalance, currency: 'USD' })

export const demoGetCategories = () => DEMO_MARKET_DATA.categories

export const demoIsMarketClosed = () => demoState.marketClosed
export const demoIsMarketSettled = () => demoState.marketSettled

export const demoGetMarketDetails = () => ({
  ...DEMO_MARKET_DATA.market,
  options: DEMO_MARKET_DATA.market.options.map((option) => ({
    ...option,
    ...(demoState.resolvedOutcomesByOptionId[+option.id] ? {
      state: 'settled',
      outcome: demoState.resolvedOutcomesByOptionId[+option.id]
    } : demoState.marketClosed ? {
      state: 'closed',
      outcome: null
    } : {})
  }))
})

export const demoGetQuestionDetails = (params = {}) => demoGetMarketDetails(params)

export const demoGetOptionGraphData = (params = {}) => {
  const optionId = params?.optionId ?? DEMO_OPTION_ID
  const optionMeta = getDemoOptionMeta(optionId)
  return {
    optionId,
    optionDataPoints: optionMeta?.optionDataPoints ?? buildGraphData(optionMeta?.yesPrice ?? 48)
  }
}

export const demoGetOptionOrderbook = (params = {}, getState) => {
  const optionId = params?.optionId ?? DEMO_OPTION_ID
  const orders = getState?.()?.predictionMarket?.demoOrders ?? []
  const orderbook = buildDemoOrderbook(optionId, orders)
  const defaultChance = getDemoOptionMeta(optionId)?.yesPrice ?? 48
  const result = { orderbook, yesPrice: getOptionChance(orderbook, defaultChance) }

  if (params?.includeMyOrders) {
    result.myLimitOrders = orders
      .filter((order) =>
        +order.userId === DEMO_USER_ID
        && +order.optionId === +optionId
        && order.orderType === ORDER_TYPES.LIMIT
        && isDemoOrderOpen(order)
      )
      .map((order) => ({ ...order, shares: +(order.remainingShares ?? order.shares) }))
  }

  return result
}

export const demoGetMyOptionPosition = (params = {}) => {
  const optionId = params?.optionId ?? DEMO_OPTION_ID
  return {
    userPosition: { ...(demoState.positionsByOptionId[+optionId] ?? emptyDemoPosition(optionId)) }
  }
}

export const demoGetMyOrders = (params = {}) => paginate(getMyLimitOrders(params), params)

export const demoGetMarketComments = (params = {}) => {
  const { parentCommentId, page = 1, limit = 20 } = params ?? {}

  if (parentCommentId != null) {
    const replies = demoState.repliesByParent[parentCommentId] ?? []
    return paginate(replies, { page, limit })
  }

  return paginate(demoState.comments, { page, limit })
}

export const demoGetMarketActivity = (params = {}) => paginate(demoState.marketActivity, params)

export const demoGetTopHolders = (params = {}) => {
  const outcome = params?.outcome === OUTCOME_TYPES.NO ? 'no' : 'yes'
  const rows = DEMO_MARKET_DATA.topHolders[outcome] ?? []

  return {
    rows,
    count: rows.length
  }
}

export const demoGetTopPositions = () => ({
  rows: DEMO_MARKET_DATA.topPositions,
  count: DEMO_MARKET_DATA.topPositions.length
})

const nextDemoOrderId = (getState) => {
  const reduxOrders = getState?.()?.predictionMarket?.demoOrders ?? []
  const ids = [...demoState.orders, ...reduxOrders].map((order) => +order.id)
  return (ids.length ? Math.max(...ids) : 1000) + 1
}

export const demoPlaceOrder = (params = {}, thunkApi) => {
  const { dispatch, getState } = thunkApi
  const { optionId, side, price, shares, amount, outcome, type, postOnly } = params ?? {}
  const normalizedShares = +shares || 0
  const normalizedAmount = +amount || 0
  const isMarketBid = type === ORDER_TYPES.MARKET && side === ORDER_SIDES.BID
  const orderShares = isMarketBid ? 0 : normalizedShares

  const pos = getDemoPosition(optionId, getState)
  let updatedPosition = { ...pos }
  let walletDelta = 0
  let orderId

  if (type === ORDER_TYPES.SPLIT) {
    if (normalizedAmount <= 0 || normalizedAmount > demoState.walletBalance) {
      return { success: false }
    }
    updatedPosition = {
      ...updatedPosition,
      yesShares: updatedPosition.yesShares + normalizedAmount,
      noShares: updatedPosition.noShares + normalizedAmount,
      avgYesPrice: updateAvgBuyPrice(updatedPosition.avgYesPrice, updatedPosition.yesShares, normalizedAmount, 50),
      avgNoPrice: updateAvgBuyPrice(updatedPosition.avgNoPrice, updatedPosition.noShares, normalizedAmount, 50)
    }
    walletDelta = -normalizedAmount
  } else if (type === ORDER_TYPES.MERGE) {
    const mergeable = Math.min(
      updatedPosition.yesShares - updatedPosition.lockedYesShares,
      updatedPosition.noShares - updatedPosition.lockedNoShares
    )
    if (normalizedShares <= 0 || normalizedShares > mergeable) {
      return { success: false }
    }
    const yesProfit = times(normalizedShares, divide(minus(50, updatedPosition.avgYesPrice ?? 0), 100))
    const noProfit = times(normalizedShares, divide(minus(50, updatedPosition.avgNoPrice ?? 0), 100))
    updatedPosition = {
      ...updatedPosition,
      yesShares: updatedPosition.yesShares - normalizedShares,
      noShares: updatedPosition.noShares - normalizedShares,
      realizedPnl: getPrecision(plus(updatedPosition.realizedPnl ?? 0, plus(yesProfit, noProfit)))
    }
    walletDelta = normalizedShares
  } else {
    if (side === ORDER_SIDES.BID && normalizedAmount > demoState.walletBalance) {
      return { success: false }
    }
    if (type === ORDER_TYPES.LIMIT && side === ORDER_SIDES.ASK) {
      const available = outcome === OUTCOME_TYPES.YES
        ? updatedPosition.yesShares - updatedPosition.lockedYesShares
        : updatedPosition.noShares - updatedPosition.lockedNoShares
      if (orderShares <= 0 || orderShares > available) {
        return { success: false }
      }
    }
    orderId = nextDemoOrderId(getState)
    const optionMeta = getDemoOptionMeta(optionId)
    if (outcome === OUTCOME_TYPES.YES && side === ORDER_SIDES.ASK) {
      updatedPosition = { ...updatedPosition, lockedYesShares: updatedPosition.lockedYesShares + orderShares }
    }
    if (outcome === OUTCOME_TYPES.NO && side === ORDER_SIDES.ASK) {
      updatedPosition = { ...updatedPosition, lockedNoShares: updatedPosition.lockedNoShares + orderShares }
    }
    if (side === ORDER_SIDES.BID) walletDelta = -normalizedAmount

    const placedOrder = {
      id: orderId,
      userId: DEMO_USER_ID,
      questionId: DEMO_QUESTION_ID,
      optionId: +optionId,
      orderType: type,
      side,
      outcome,
      price,
      shares: orderShares,
      remainingShares: orderShares,
      remainingAmount: normalizedAmount,
      amount: normalizedAmount,
      toRefund: side === ORDER_SIDES.BID ? normalizedAmount : 0,
      state: ORDER_STATES.OPEN,
      postOnly,
      questionTitle: DEMO_MARKET_DATA.market.title,
      optionTitle: optionMeta?.title ?? getTranslation('demoOutcome'),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }
    if (type === ORDER_TYPES.LIMIT && postOnly && wouldLimitCross(placedOrder, getState().predictionMarket.demoOrders ?? [])) {
      return { success: false }
    }
    const demoOrderEmitData = {
      type: USER_ACTIVITY_TYPES.ORDER_PLACED,
      log: { order: placedOrder },
      questionId: DEMO_QUESTION_ID,
      optionId: +optionId,
      userId: DEMO_USER_ID,
      myUserId: DEMO_USER_ID
    }
    dispatch(userActivityReceived(demoOrderEmitData))
    dispatch(marketActivityReceived(demoOrderEmitData))
    recordDemoMarketActivity(demoOrderEmitData)
    dispatch(demoOrdersAdd(placedOrder))
    demoState.orders.push(placedOrder)
    setDemoPosition(optionId, updatedPosition)
    matchDemoOrders({ dispatch, getState })
    syncDemoBookToStore(dispatch, getState().predictionMarket.demoOrders, getState)
    syncDemoPosition(dispatch, demoState.positionsByOptionId[+optionId])
    if (walletDelta) {
      demoState.walletBalance += walletDelta
      dispatch(setDemoWallet(demoGetWallet()))
    }
    return { success: true, orderId, credited: false }
  }

  setDemoPosition(optionId, updatedPosition)
  if (walletDelta) {
    demoState.walletBalance += walletDelta
    dispatch(setDemoWallet(demoGetWallet()))
  }
  syncDemoPosition(dispatch, updatedPosition)

  return { success: true, orderId, credited: type === ORDER_TYPES.MERGE }
}

export const matchDemoOrders = ({ dispatch, getState }) => {
  if (demoState.marketClosed) return
  const positionsBefore = JSON.stringify(demoState.positionsByOptionId)
  for (let i = 0; i < MAX_DEMO_MATCH_ITERATIONS; i++) {
    if (!matchDemoOrdersOnce({ dispatch, getState })) break
  }
  closeExhaustedMarketOrders({ dispatch, getState })
  if (JSON.stringify(demoState.positionsByOptionId) !== positionsBefore) {
    const chosenId = getState().predictionMarket.chosenMarket?.chosenOptionId ?? DEMO_OPTION_ID
    syncDemoPosition(dispatch, demoState.positionsByOptionId[+chosenId] ?? emptyDemoPosition(chosenId))
  }
}

const matchDemoOrdersOnce = ({ dispatch, getState }) => {
  const { demoOrders, myOptionPosition } = getState().predictionMarket
  let orders = (demoOrders ?? []).map((order) => ({ ...order }))
  const open = orders.filter(isDemoOrderOpen)
  if (!open.length) return false

  const optionIds = [...new Set(open.map((order) => +order.optionId))]

  for (const matchOptionId of optionIds) {
    const optionOpen = open.filter((order) => +order.optionId === matchOptionId)
    const limits = (side, outcome) => sortLimits(optionOpen, side, outcome, matchOptionId)

    let a = null
    let b = null
    let kind = null
    let price = 0
    let yesPrice = 0
    let noPrice = 0

    for (const outcome of [OUTCOME_TYPES.YES, OUTCOME_TYPES.NO]) {
      const bid = limits(ORDER_SIDES.BID, outcome)[0]
      const ask = limits(ORDER_SIDES.ASK, outcome)[0]
      if (bid && ask && +ask.price <= +bid.price && +bid.userId !== +ask.userId) {
        a = bid
        b = ask
        kind = 'trade'
        price = +bid.id < +ask.id ? +bid.price : +ask.price
        break
      }
    }

    if (!a) {
      const yesBid = limits(ORDER_SIDES.BID, OUTCOME_TYPES.YES)[0]
      const noBid = limits(ORDER_SIDES.BID, OUTCOME_TYPES.NO)[0]
      if (yesBid && noBid && +yesBid.price + +noBid.price >= 100 && +yesBid.userId !== +noBid.userId) {
        a = yesBid
        b = noBid
        kind = 'mint'
        yesPrice = +yesBid.id < +noBid.id ? +yesBid.price : 100 - +noBid.price
        noPrice = 100 - yesPrice
      }
    }

    if (!a) {
      const market = optionOpen.filter((order) => order.orderType === ORDER_TYPES.MARKET).sort((x, y) => +x.id - +y.id)[0]
      if (market) {
        if (market.side === ORDER_SIDES.BID) {
          const ask = limits(ORDER_SIDES.ASK, market.outcome)[0]
          const oppBid = limits(ORDER_SIDES.BID, market.outcome === OUTCOME_TYPES.YES ? OUTCOME_TYPES.NO : OUTCOME_TYPES.YES)[0]
          const askImplied = ask ? +ask.price : Infinity
          const mintImplied = oppBid ? 100 - +oppBid.price : Infinity
          if (mintImplied < askImplied && oppBid && +oppBid.userId !== +market.userId) {
            a = market
            b = oppBid
            kind = 'mint'
            if (market.outcome === OUTCOME_TYPES.YES) {
              yesPrice = 100 - +oppBid.price
              noPrice = +oppBid.price
            } else {
              yesPrice = +oppBid.price
              noPrice = 100 - +oppBid.price
            }
          } else if (ask && +ask.userId !== +market.userId) {
            a = market
            b = ask
            kind = 'trade'
            price = +ask.price
          }
        } else {
          const bid = limits(ORDER_SIDES.BID, market.outcome)[0]
          if (bid && +bid.userId !== +market.userId) {
            a = market
            b = bid
            kind = 'trade'
            price = +bid.price
          }
        }
      }
    }

    if (!a || !b) continue

    const tradeQty = () => {
      if (kind === 'mint') {
        const marketOrder = [a, b].find((order) => order.orderType === ORDER_TYPES.MARKET)
        if (marketOrder?.side === ORDER_SIDES.BID) {
          const limitOrder = marketOrder === a ? b : a
          const p = marketOrder.outcome === OUTCOME_TYPES.YES ? yesPrice : noPrice
          return Math.min(+limitOrder.remainingShares, Math.floor(+marketOrder.remainingAmount / spendPerShare(p)))
        }
        return Math.min(+a.remainingShares, +b.remainingShares)
      }
      if (a.orderType === ORDER_TYPES.MARKET && a.side === ORDER_SIDES.BID) {
        return Math.min(+b.remainingShares, Math.floor(+a.remainingAmount / spendPerShare(price)))
      }
      if (b.orderType === ORDER_TYPES.MARKET && b.side === ORDER_SIDES.BID) {
        return Math.min(+a.remainingShares, Math.floor(+b.remainingAmount / spendPerShare(price)))
      }
      return Math.min(+a.remainingShares, +b.remainingShares)
    }
    const qty = tradeQty()
    if (qty <= 0) continue

    if (kind === 'trade') {
      const bid = a.side === ORDER_SIDES.BID ? a : b
      const ask = a.side === ORDER_SIDES.ASK ? a : b
      const taker = +bid.id > +ask.id ? bid : ask
      if (taker.orderType === ORDER_TYPES.LIMIT && taker.postOnly) continue
    }

    const preA = { ...a }
    const preB = { ...b }
    let fillRefund = 0

    const applyFill = (order, filled, tradePrice) => {
      if (order.orderType === ORDER_TYPES.MARKET && order.side === ORDER_SIDES.BID) {
        const remainingAmount = Math.max(0, +order.remainingAmount - filled * spendPerShare(tradePrice))
        const done = remainingAmount <= MIN_MARKET_REMAINING
        if (done && remainingAmount > 0) fillRefund += remainingAmount
        return {
          ...order,
          remainingAmount: done ? 0 : remainingAmount,
          state: done ? ORDER_STATES.FILLED : ORDER_STATES.PARTIALLY_FILLED
        }
      }
      if (order.orderType === ORDER_TYPES.MARKET && order.side === ORDER_SIDES.ASK) {
        const remainingShares = Math.max(0, +order.remainingShares - filled)
        const done = remainingShares <= MIN_MARKET_REMAINING
        return {
          ...order,
          remainingShares: done ? 0 : remainingShares,
          state: done ? ORDER_STATES.FILLED : ORDER_STATES.PARTIALLY_FILLED
        }
      }
      const remainingShares = Math.max(0, +order.remainingShares - filled)
      const reservedCost = order.side === ORDER_SIDES.BID ? filled * +order.price / 100 : 0
      return {
        ...order,
        remainingShares,
        remainingAmount: order.side === ORDER_SIDES.BID
          ? Math.max(0, +order.remainingAmount - reservedCost)
          : order.remainingAmount,
        state: remainingShares > 0 ? ORDER_STATES.PARTIALLY_FILLED : ORDER_STATES.FILLED
      }
    }

    orders = orders.map((order) => {
      if (+order.id === +a.id) return applyFill(order, qty, kind === 'mint' ? (order.outcome === OUTCOME_TYPES.YES ? yesPrice : noPrice) : price)
      if (+order.id === +b.id) return applyFill(order, qty, kind === 'mint' ? (order.outcome === OUTCOME_TYPES.YES ? yesPrice : noPrice) : price)
      return order
    })

    const filledA = orders.find((order) => +order.id === +a.id)
    const filledB = orders.find((order) => +order.id === +b.id)
    const optionId = matchOptionId
    const chosenId = getState().predictionMarket.chosenMarket?.chosenOptionId
    const basePos = (+chosenId === optionId ? (demoState.positionsByOptionId[optionId] ?? myOptionPosition) : demoState.positionsByOptionId[optionId]) ?? emptyDemoPosition(optionId)
    let pos = {
      optionId,
      yesShares: basePos.yesShares ?? 0,
      noShares: basePos.noShares ?? 0,
      lockedYesShares: basePos.lockedYesShares ?? 0,
      lockedNoShares: basePos.lockedNoShares ?? 0,
      avgYesPrice: basePos.avgYesPrice ?? 0,
      avgNoPrice: basePos.avgNoPrice ?? 0,
      realizedPnl: basePos.realizedPnl ?? 0
    }
    let walletDelta = 0

    const pushActivity = (type, trade, userId) => {
      const payload = {
        type,
        questionId: DEMO_QUESTION_ID,
        optionId,
        userId,
        myUserId: DEMO_USER_ID,
        log: { trade: { ...trade, optionId, createdAt: new Date().toISOString() } }
      }
      recordDemoMarketActivity(payload)
      dispatch(marketActivityReceived(payload))
      if (+userId === DEMO_USER_ID || +trade.buyerId === DEMO_USER_ID || +trade.sellerId === DEMO_USER_ID) {
        dispatch(userActivityReceived(payload))
      }
    }

    const closeOrder = (order) => {
      if (!order) return
      if (order.orderType === ORDER_TYPES.MARKET) {
        const done = order.state === ORDER_STATES.FILLED
          || (order.side === ORDER_SIDES.BID ? +order.remainingAmount <= MIN_MARKET_REMAINING : +order.remainingShares <= MIN_MARKET_REMAINING)
        if (!done) return
      } else if (order.state !== ORDER_STATES.FILLED) {
        return
      }
      emitDemoOrderClosed(dispatch, {
        ...order,
        remainingAmount: 0,
        remainingShares: 0,
        state: ORDER_STATES.FILLED
      })
    }

    if (kind === 'trade') {
      const bid = preA.side === ORDER_SIDES.BID ? preA : preB
      const ask = preA.side === ORDER_SIDES.ASK ? preA : preB

      const trade = {
        outcome: bid.outcome,
        price,
        quantity: qty,
        buyerId: bid.userId,
        sellerId: ask.userId,
        buyerName: +bid.userId === DEMO_USER_ID ? getTranslation('demo') : getTranslation('system'),
        sellerName: +ask.userId === DEMO_USER_ID ? getTranslation('demo') : getTranslation('system')
      }
      pushActivity(USER_ACTIVITY_TYPES.BOUGHT_SHARES, trade, bid.userId)
      pushActivity(USER_ACTIVITY_TYPES.SOLD_SHARES, trade, ask.userId)

      if (+bid.userId === DEMO_USER_ID) {
        if (bid.outcome === OUTCOME_TYPES.YES) {
          pos = {
            ...pos,
            yesShares: pos.yesShares + qty,
            avgYesPrice: updateAvgBuyPrice(pos.avgYesPrice, pos.yesShares, qty, price)
          }
        } else {
          pos = {
            ...pos,
            noShares: pos.noShares + qty,
            avgNoPrice: updateAvgBuyPrice(pos.avgNoPrice, pos.noShares, qty, price)
          }
        }
        if (bid.orderType === ORDER_TYPES.LIMIT && +bid.price > +price) {
          walletDelta += qty * (+bid.price - +price) / 100
        }
      }
      if (+ask.userId === DEMO_USER_ID) {
        walletDelta += qty * price / 100
        pos = ask.outcome === OUTCOME_TYPES.YES
          ? {
            ...pos,
            realizedPnl: applySellRealizedPnl(pos, OUTCOME_TYPES.YES, qty, price),
            yesShares: Math.max(0, pos.yesShares - qty),
            lockedYesShares: Math.max(0, pos.lockedYesShares - qty)
          }
          : {
            ...pos,
            realizedPnl: applySellRealizedPnl(pos, OUTCOME_TYPES.NO, qty, price),
            noShares: Math.max(0, pos.noShares - qty),
            lockedNoShares: Math.max(0, pos.lockedNoShares - qty)
          }
      }
      closeOrder(filledA.orderType === ORDER_TYPES.MARKET ? filledA : null)
      closeOrder(filledB.orderType === ORDER_TYPES.MARKET ? filledB : null)
      closeOrder(filledA.orderType === ORDER_TYPES.LIMIT && filledA.state === ORDER_STATES.FILLED ? filledA : null)
      closeOrder(filledB.orderType === ORDER_TYPES.LIMIT && filledB.state === ORDER_STATES.FILLED ? filledB : null)
    } else {
      const yesBid = preA.outcome === OUTCOME_TYPES.YES ? preA : preB
      const noBid = preA.outcome === OUTCOME_TYPES.NO ? preA : preB

      const yesTrade = {
        outcome: OUTCOME_TYPES.YES,
        price: yesPrice,
        quantity: qty,
        buyerId: yesBid.userId,
        buyerName: +yesBid.userId === DEMO_USER_ID ? getTranslation('demo') : getTranslation('system')
      }
      const noTrade = {
        outcome: OUTCOME_TYPES.NO,
        price: noPrice,
        quantity: qty,
        buyerId: noBid.userId,
        buyerName: +noBid.userId === DEMO_USER_ID ? getTranslation('demo') : getTranslation('system')
      }
      pushActivity(USER_ACTIVITY_TYPES.BOUGHT_SHARES, yesTrade, yesTrade.buyerId)
      pushActivity(USER_ACTIVITY_TYPES.BOUGHT_SHARES, noTrade, noTrade.buyerId)
      if (+yesTrade.buyerId === DEMO_USER_ID) {
        pos = {
          ...pos,
          yesShares: pos.yesShares + qty,
          avgYesPrice: updateAvgBuyPrice(pos.avgYesPrice, pos.yesShares, qty, yesPrice)
        }
      }
      if (+noTrade.buyerId === DEMO_USER_ID) {
        pos = {
          ...pos,
          noShares: pos.noShares + qty,
          avgNoPrice: updateAvgBuyPrice(pos.avgNoPrice, pos.noShares, qty, noPrice)
        }
      }
      closeOrder(filledA.orderType === ORDER_TYPES.MARKET ? filledA : null)
      closeOrder(filledB.orderType === ORDER_TYPES.MARKET ? filledB : null)
      closeOrder(filledA.orderType === ORDER_TYPES.LIMIT && filledA.state === ORDER_STATES.FILLED ? filledA : null)
      closeOrder(filledB.orderType === ORDER_TYPES.LIMIT && filledB.state === ORDER_STATES.FILLED ? filledB : null)
    }

    setDemoPosition(optionId, pos)
    demoState.orders = orders.filter((order) => order.userId === DEMO_USER_ID)
    if (walletDelta || fillRefund) {
      demoState.walletBalance += walletDelta + fillRefund
      dispatch(setDemoWallet(demoGetWallet()))
    }
    dispatch(demoOrdersReset(orders))
    syncDemoBookToStore(dispatch, orders, getState)
    return true
  }

  return false
}

export const demoCancelLimitOrder = ({ orderId, optionId }, thunkApi) => {
  const { dispatch, getState } = thunkApi

  let existing = demoState.orders.find((o) => +o.id === +orderId && +o.optionId === +optionId)
  if (!existing) {
    existing = (getState().predictionMarket.demoOrders ?? []).find(
      (o) => +o.id === +orderId && +o.optionId === +optionId && +o.userId === DEMO_USER_ID
    )
  }
  if (!existing || existing.orderType !== ORDER_TYPES.LIMIT) {
    return { success: false }
  }
  if (![ORDER_STATES.OPEN, ORDER_STATES.PARTIALLY_FILLED].includes(existing.state)) {
    return { success: false }
  }

  const remaining = +existing.remainingShares
  if (remaining <= 0) return { success: false }

  const order = { ...existing, state: ORDER_STATES.CLOSED, remainingShares: 0, remainingAmount: 0 }
  const orderIndex = demoState.orders.findIndex((o) => +o.id === +orderId)
  if (orderIndex >= 0) demoState.orders[orderIndex] = order

  const pos = getDemoPosition(optionId, getState)
  let updatedPosition = { ...pos }
  let walletDelta = 0

  if (order.side === ORDER_SIDES.ASK) {
    updatedPosition = order.outcome === OUTCOME_TYPES.YES
      ? { ...updatedPosition, lockedYesShares: Math.max(0, updatedPosition.lockedYesShares - remaining) }
      : { ...updatedPosition, lockedNoShares: Math.max(0, updatedPosition.lockedNoShares - remaining) }
  } else {
    walletDelta = +existing.remainingAmount || 0
  }

  setDemoPosition(optionId, updatedPosition)
  if (walletDelta) {
    demoState.walletBalance += walletDelta
    dispatch(setDemoWallet(demoGetWallet()))
  }

  emitDemoOrderClosed(dispatch, order)
  dispatch(demoOrdersRemove({ orderId }))

  syncDemoBookToStore(dispatch, getState().predictionMarket.demoOrders, getState)
  syncDemoPosition(dispatch, updatedPosition)

  return { success: true, order }
}

export const demoAddComment = (payload = {}) => {
  const { comment, parentCommentId } = payload
  const trimmed = comment?.trim()
  if (!trimmed) return { success: false, error: getTranslation('commentCannotBeEmpty') }

  const newComment = {
    id: demoState.nextCommentId++,
    userId: DEMO_USER_ID,
    userCode: 'demo-user',
    thumbnailUrl: null,
    comment: trimmed,
    parentCommentId: parentCommentId ?? null,
    likes: 0,
    likedByMe: false,
    createdAt: new Date().toISOString(),
    replies: [],
    repliesCount: 0
  }

  if (parentCommentId != null) {
    demoState.repliesByParent[parentCommentId] = [
      newComment,
      ...(demoState.repliesByParent[parentCommentId] ?? [])
    ]
    const parent = demoState.comments.find((item) => +item.id === +parentCommentId)
    if (parent) {
      parent.repliesCount = (parent.repliesCount ?? 0) + 1
      parent.replies = [newComment, ...(parent.replies ?? [])]
    }
  } else {
    demoState.comments.unshift(newComment)
  }

  return { success: true, comment: newComment }
}

export const demoDeleteComment = (payload = {}) => {
  const { commentId, parentCommentId } = payload
  if (parentCommentId != null) {
    const replies = demoState.repliesByParent[parentCommentId] ?? []
    const target = replies.find((reply) => +reply.id === +commentId)
    if (!target || +target.userId !== DEMO_USER_ID) return { success: false, error: getTranslation('commentNotFound') }
    demoState.repliesByParent[parentCommentId] = replies.filter((reply) => +reply.id !== +commentId)
    const parent = demoState.comments.find((item) => +item.id === +parentCommentId)
    if (parent) {
      parent.replies = (parent.replies ?? []).filter((reply) => +reply.id !== +commentId)
      parent.repliesCount = Math.max(0, (parent.repliesCount ?? 0) - 1)
    }
    return { success: true, commentId, parentCommentId }
  }

  const index = demoState.comments.findIndex((item) => +item.id === +commentId)
  if (index === -1 || +demoState.comments[index].userId !== DEMO_USER_ID) {
    return { success: false, error: getTranslation('commentNotFound') }
  }
  demoState.comments.splice(index, 1)
  return { success: true, commentId, parentCommentId: null }
}

export const demoLikeToggle = (payload = {}) => {
  const { commentId } = payload
  let result = null

  const updateComment = (item) => {
    if (+item.id !== +commentId) return item
    const likedByMe = !item.likedByMe
    const likes = Math.max(0, (item.likes ?? 0) + (likedByMe ? 1 : -1))
    result = { commentId: +commentId, likes, liked: likedByMe }
    return { ...item, likedByMe, likes }
  }

  demoState.comments = demoState.comments.map((comment) => {
    if (+comment.id === +commentId) return updateComment(comment)
    if (comment.replies?.length) {
      return { ...comment, replies: comment.replies.map(updateComment) }
    }
    return comment
  })

  if (!result) {
    for (const parentId of Object.keys(demoState.repliesByParent)) {
      demoState.repliesByParent[parentId] = demoState.repliesByParent[parentId].map((reply) => {
        if (+reply.id === +commentId) return updateComment(reply)
        return reply
      })
    }
  }

  return result ?? { success: false }
}

export const demoGetActiveMarketOrder = (getState) => {
  const orders = getState?.()?.predictionMarket?.demoOrders ?? []
  return orders.find(
    (order) => +order.userId === DEMO_USER_ID
      && order.orderType === ORDER_TYPES.MARKET
      && isDemoOrderOpen(order)
  ) ?? null
}

const closeDemoUserOrders = ({ dispatch, getState }) => {
  const closedUserOrders = []
  let walletDelta = 0

  for (const order of (getState().predictionMarket.demoOrders ?? [])) {
    if (+order.userId !== DEMO_USER_ID) continue

    let closedOrder = { ...order }
    const isActive = [ORDER_STATES.OPEN, ORDER_STATES.PARTIALLY_FILLED, ORDER_STATES.FILLED].includes(order.state)

    if (isActive) {
      if (order.side === ORDER_SIDES.BID) {
        const refund = Math.max(0, +(order.toRefund ?? order.remainingAmount ?? 0))
        if (refund > 0) walletDelta += refund
        closedOrder = {
          ...closedOrder,
          toRefund: 0,
          remainingAmount: 0,
          remainingShares: 0,
          state: ORDER_STATES.CLOSED
        }
      } else {
        const optionId = +order.optionId
        const pos = demoState.positionsByOptionId[optionId] ?? emptyDemoPosition(optionId)
        const remaining = +order.remainingShares || 0
        setDemoPosition(
          optionId,
          order.outcome === OUTCOME_TYPES.YES
            ? { ...pos, lockedYesShares: Math.max(0, pos.lockedYesShares - remaining) }
            : { ...pos, lockedNoShares: Math.max(0, pos.lockedNoShares - remaining) }
        )
        closedOrder = {
          ...closedOrder,
          remainingShares: 0,
          remainingAmount: 0,
          state: ORDER_STATES.CLOSED
        }
      }
      emitDemoOrderClosed(dispatch, closedOrder)
    }

    closedUserOrders.push(closedOrder)
  }

  demoState.orders = closedUserOrders
  return { closedUserOrders, walletDelta }
}

export const demoCloseMarket = ({ dispatch, getState }) => {
  if (demoState.marketClosed) return { success: false }

  const { closedUserOrders, walletDelta } = closeDemoUserOrders({ dispatch, getState })

  demoState.marketClosed = true

  if (walletDelta) {
    demoState.walletBalance += walletDelta
    dispatch(setDemoWallet(demoGetWallet()))
  }

  const closedOptions = DEMO_OPTION_IDS.map((id) => ({ id: +id, state: 'closed' }))
  dispatch(demoOrdersReset(closedUserOrders))
  dispatch(demoMarketClosed({ userOrders: closedUserOrders, closedOptions }))

  const chosenId = getState().predictionMarket.chosenMarket?.chosenOptionId ?? DEMO_OPTION_ID
  syncDemoBookToStore(dispatch, closedUserOrders, getState)
  syncDemoPosition(dispatch, demoState.positionsByOptionId[+chosenId] ?? emptyDemoPosition(chosenId))

  return { success: true, walletDelta }
}

export const demoSettleMarket = ({ dispatch, getState, outcome = OUTCOME_TYPES.YES }) => {
  if (!demoState.marketClosed) return { success: false }

  const optionId = getState().predictionMarket.chosenMarket?.chosenOptionId ?? DEMO_OPTION_ID
  if (demoState.resolvedOutcomesByOptionId[+optionId]) return { success: false }

  const normalizedOutcome = outcome === OUTCOME_TYPES.NO ? OUTCOME_TYPES.NO : OUTCOME_TYPES.YES
  demoState.resolvedOutcomesByOptionId[+optionId] = normalizedOutcome

  const pos = demoState.positionsByOptionId[+optionId] ?? emptyDemoPosition(optionId)
  const winningShares = normalizedOutcome === OUTCOME_TYPES.YES ? pos.yesShares : pos.noShares
  const resolutionProfit = computeResolutionProfit(pos, normalizedOutcome)
  const totalRealizedPnl = getPrecision(plus(pos.realizedPnl ?? 0, resolutionProfit))
  const payout = winningShares

  const optionMeta = getDemoOptionMeta(optionId)
  dispatch(userActivityReceived({
    type: USER_ACTIVITY_TYPES.POSITION_RESOLVED,
    questionId: DEMO_QUESTION_ID,
    optionId: +optionId,
    userId: DEMO_USER_ID,
    myUserId: DEMO_USER_ID,
    createdAt: new Date().toISOString(),
    log: {
      questionTitle: DEMO_MARKET_DATA.market.title,
      optionTitle: optionMeta?.title,
      position: {
        outcome: normalizedOutcome,
        payout,
        winningShares
      }
    }
  }))

  setDemoPosition(optionId, {
    ...emptyDemoPosition(optionId),
    avgYesPrice: pos.avgYesPrice ?? 0,
    avgNoPrice: pos.avgNoPrice ?? 0,
    realizedPnl: totalRealizedPnl,
    state: USER_POSITION_STATES.RESOLVED
  })

  demoState.marketSettled = DEMO_OPTION_IDS.every((id) => demoState.resolvedOutcomesByOptionId[+id])

  if (payout) {
    demoState.walletBalance += payout
    dispatch(setDemoWallet(demoGetWallet()))
    dispatch(walletBalanceNotificationReceived({
      balance: demoState.walletBalance,
      currency: 'USD',
      type: 'resolve'
    }))
  }

  dispatch(demoMarketSettled({
    settledOptions: [{ id: +optionId, state: OPTION_STATES.SETTLED, outcome: normalizedOutcome }]
  }))

  syncDemoPosition(dispatch, demoState.positionsByOptionId[+optionId] ?? emptyDemoPosition(optionId))

  return { success: true, walletDelta: payout }
}

/* -------------------------------------------------------------------------- */
/* Demo walkthrough (coach marks)                                              */
/* -------------------------------------------------------------------------- */

export const DEMO_WALKTHROUGH_STORAGE_KEY = 'pm-demo-walkthrough-dismissed'

const countOpenUserLimits = (demoOrders = [], optionId) =>
  demoOrders.filter((order) =>
    +order.userId === DEMO_USER_ID
    && order.orderType === ORDER_TYPES.LIMIT
    && isDemoOrderOpen(order)
    && (optionId == null || +order.optionId === +optionId)
  ).length

const countOpenUserPostOnly = (demoOrders = [], optionId) =>
  demoOrders.filter((order) =>
    +order.userId === DEMO_USER_ID
    && order.orderType === ORDER_TYPES.LIMIT
    && order.postOnly
    && isDemoOrderOpen(order)
    && (optionId == null || +order.optionId === +optionId)
  ).length

const getPositionShareTotals = (position) => ({
  yesShares: +(position?.yesShares ?? 0),
  noShares: +(position?.noShares ?? 0)
})

export const isDemoWalkthroughDismissed = () => {
  try {
    return sessionStorage.getItem(DEMO_WALKTHROUGH_STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

export const setDemoWalkthroughDismissed = (dismissed = true) => {
  try {
    if (dismissed) sessionStorage.setItem(DEMO_WALKTHROUGH_STORAGE_KEY, '1')
    else sessionStorage.removeItem(DEMO_WALKTHROUGH_STORAGE_KEY)
  } catch {
    /* ignore storage failures */
  }
}

export const captureDemoWalkthroughSnapshot = (predictionMarketState = {}) => {
  const optionId = predictionMarketState.chosenMarket?.chosenOptionId
  const demoOrders = predictionMarketState.demoOrders ?? []
  const position = predictionMarketState.myOptionPosition
  const shares = getPositionShareTotals(position)
  const book = predictionMarketState.chosenMarket?.orderbook
  const askLevels = Object.keys(book?.yes?.ask ?? {}).length + Object.keys(book?.no?.ask ?? {}).length

  return {
    optionId,
    openLimitCount: countOpenUserLimits(demoOrders, optionId),
    postOnlyCount: countOpenUserPostOnly(demoOrders, optionId),
    yesShares: shares.yesShares,
    noShares: shares.noShares,
    askLevels,
    isDemoMarketClosed: Boolean(predictionMarketState.isDemoMarketClosed),
    isDemoMarketSettled: Boolean(predictionMarketState.isDemoMarketSettled),
    walletBalance: demoState.walletBalance
  }
}

/**
 * Clears resting asks for the chosen option so a market buy must mint
 * against opposite-side bids (teaches "no asks, still get shares").
 */
export const demoPrepareEmptyAsksForMint = ({ dispatch, getState }) => {
  const state = getState?.()?.predictionMarket ?? {}
  const optionId = state.chosenMarket?.chosenOptionId ?? DEMO_OPTION_ID
  const before = state.demoOrders ?? []
  const next = before.filter((order) => !(
    +order.optionId === +optionId
    && order.side === ORDER_SIDES.ASK
    && order.orderType === ORDER_TYPES.LIMIT
  ))
  const removed = before.length - next.length
  dispatch(demoOrdersReset(next))
  syncDemoBookToStore(dispatch, next, getState)
  return { success: true, optionId: +optionId, removed }
}

/** Places a resting post-only bid if the user has none (so cancel/orderbook steps can work). */
export const demoEnsureRestingLimitForGuide = ({ dispatch, getState }) => {
  const state = getState?.()?.predictionMarket ?? {}
  const optionId = state.chosenMarket?.chosenOptionId ?? DEMO_OPTION_ID
  if (countOpenUserLimits(state.demoOrders ?? [], optionId) > 0) {
    return { success: true, created: false }
  }

  const price = 40
  const shares = 10
  const amount = getPrecision(divide(times(price, shares), 100))
  const result = demoPlaceOrder({
    optionId,
    side: ORDER_SIDES.BID,
    outcome: OUTCOME_TYPES.YES,
    type: ORDER_TYPES.LIMIT,
    price,
    shares,
    amount,
    postOnly: true
  }, { dispatch, getState })

  return { ...result, created: Boolean(result?.success) }
}

const setNativeInputValue = (input, value) => {
  if (!input) return
  const setter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set
  setter?.call(input, String(value))
  input.dispatchEvent(new Event('input', { bubbles: true }))
  input.dispatchEvent(new Event('change', { bubbles: true }))
}

const clickMatchingButton = (root, pattern) => {
  const buttons = [...(root?.querySelectorAll('button') ?? [])]
  const match = buttons.find((button) => pattern.test((button.textContent || '').trim()))
  match?.click()
  return Boolean(match)
}

const clickBuySide = (panel) => {
  const buyBtn = panel.querySelector('.order-panel-side-toggle-btn--buy')
    || panel.querySelector('.order-panel-side-toggle .order-panel-side-toggle-btn')
  if (buyBtn) {
    buyBtn.click()
    return true
  }
  return clickMatchingButton(panel, /^(buy|comprar|买入)$/i)
}

/**
 * Prefills the live order panel so the user only needs to confirm/submit.
 * Uses native input setters so React controlled fields update.
 */
export const demoWalkthroughSeedUi = (stepId) => {
  const panel = document.getElementById('order-panel')
  if (!panel) return false

  switch (stepId) {
    case 'limit':
    case 'postOnly': {
      clickBuySide(panel)
      const priceInput = panel.querySelector('.order-panel-stepper-input--cents')
        || panel.querySelector('.order-panel-stepper-input--dollars')
      const shareInputs = [...panel.querySelectorAll('.order-panel-stepper-input')]
      const shares = shareInputs.find((input) => input !== priceInput) ?? shareInputs[1]
      setNativeInputValue(priceInput, '45')
      setNativeInputValue(shares, '10')
      if (stepId === 'postOnly') {
        const checkbox = document.querySelector('#order-post-only input[type="checkbox"]')
          || panel.querySelector('input[type="checkbox"]')
        if (checkbox && !checkbox.checked) checkbox.click()
      }
      return true
    }
    case 'market':
    case 'mint': {
      clickBuySide(panel)
      setNativeInputValue(panel.querySelector('.order-panel-stepper-input'), '5')
      return true
    }
    case 'split': {
      setNativeInputValue(panel.querySelector('.order-panel-stepper-input'), '10')
      return true
    }
    case 'merge': {
      setNativeInputValue(panel.querySelector('.order-panel-stepper-input'), '5')
      return true
    }
    default:
      return false
  }
}

export const isDemoWalkthroughStepComplete = (stepId, predictionMarketState = {}, snapshot = {}) => {
  const optionId = predictionMarketState.chosenMarket?.chosenOptionId ?? snapshot.optionId
  const demoOrders = predictionMarketState.demoOrders ?? []
  const shares = getPositionShareTotals(predictionMarketState.myOptionPosition)
  const book = predictionMarketState.chosenMarket?.orderbook
  const askLevels = Object.keys(book?.yes?.ask ?? {}).length + Object.keys(book?.no?.ask ?? {}).length

  switch (stepId) {
    case 'limit':
      return countOpenUserLimits(demoOrders, optionId) > (snapshot.openLimitCount ?? 0)
    case 'orderbook':
      return countOpenUserLimits(demoOrders, optionId) > 0
        || document.querySelector('.book-row--user-order') != null
    case 'postOnly':
      return countOpenUserPostOnly(demoOrders, optionId) > (snapshot.postOnlyCount ?? 0)
    case 'cancel':
      return countOpenUserLimits(demoOrders, optionId) < (snapshot.openLimitCount ?? 0)
    case 'market':
      return (shares.yesShares + shares.noShares) > ((snapshot.yesShares ?? 0) + (snapshot.noShares ?? 0) + 0.001)
    case 'mint':
      return askLevels === 0
        && (shares.yesShares + shares.noShares) > ((snapshot.yesShares ?? 0) + (snapshot.noShares ?? 0) + 0.001)
    case 'split':
      return shares.yesShares > (snapshot.yesShares ?? 0) + 0.001
        && shares.noShares > (snapshot.noShares ?? 0) + 0.001
    case 'merge': {
      const beforePair = Math.min(snapshot.yesShares ?? 0, snapshot.noShares ?? 0)
      const afterPair = Math.min(shares.yesShares, shares.noShares)
      return afterPair < beforePair - 0.001
    }
    case 'resolve':
      return Boolean(predictionMarketState.isDemoMarketClosed || predictionMarketState.isDemoMarketSettled)
    default:
      return false
  }
}

/**
 * Spotlight targets use plain element ids.
 * Next is never gated — seed/prepare only make the relevant UI ready to explore.
 * `autoAdvance` still moves forward if the user completes the action.
 */
export const DEMO_WALKTHROUGH_STEPS = [
  {
    id: 'welcome',
    titleKey: 'demoGuideWelcomeTitle',
    bodyKey: 'demoGuideWelcomeBody',
    hintKey: 'demoGuideWelcomeHint',
    targetId: 'market-detail-header',
    placement: 'bottom'
  },
  {
    id: 'limit',
    titleKey: 'demoGuideLimitTitle',
    bodyKey: 'demoGuideLimitBody',
    hintKey: 'demoGuideLimitHint',
    targetId: 'order-panel-form',
    placement: 'left',
    ensureTradeOpen: true,
    focusId: 'order-tab-limit',
    seedUi: true,
    autoAdvance: true
  },
  {
    id: 'orderbook',
    titleKey: 'demoGuideOrderbookTitle',
    bodyKey: 'demoGuideOrderbookBody',
    hintKey: 'demoGuideOrderbookHint',
    targetId: 'option-orderbook',
    placement: 'right',
    focusId: 'option-tab-orderbook',
    prepare: 'ensureLimit'
  },
  {
    id: 'postOnly',
    titleKey: 'demoGuidePostOnlyTitle',
    bodyKey: 'demoGuidePostOnlyBody',
    hintKey: 'demoGuidePostOnlyHint',
    targetId: 'order-post-only',
    placement: 'left',
    ensureTradeOpen: true,
    focusId: 'order-tab-limit',
    seedUi: true,
    autoAdvance: true
  },
  {
    id: 'cancel',
    titleKey: 'demoGuideCancelTitle',
    bodyKey: 'demoGuideCancelBody',
    hintKey: 'demoGuideCancelHint',
    targetId: 'option-my-orders',
    placement: 'bottom',
    focusId: 'option-tab-my-position',
    prepare: 'ensureLimit',
    autoAdvance: true
  },
  {
    id: 'market',
    titleKey: 'demoGuideMarketTitle',
    bodyKey: 'demoGuideMarketBody',
    hintKey: 'demoGuideMarketHint',
    targetId: 'order-panel-form',
    placement: 'left',
    ensureTradeOpen: true,
    focusId: 'order-tab-market',
    seedUi: true,
    autoAdvance: true
  },
  {
    id: 'mint',
    titleKey: 'demoGuideMintTitle',
    bodyKey: 'demoGuideMintBody',
    hintKey: 'demoGuideMintHint',
    targetId: 'order-panel-form',
    placement: 'left',
    ensureTradeOpen: true,
    focusId: 'order-tab-market',
    prepare: 'emptyAsks',
    seedUi: true,
    autoAdvance: true
  },
  {
    id: 'split',
    titleKey: 'demoGuideSplitTitle',
    bodyKey: 'demoGuideSplitBody',
    hintKey: 'demoGuideSplitHint',
    targetId: 'order-panel-form',
    placement: 'left',
    ensureTradeOpen: true,
    focusId: 'order-tab-split',
    seedUi: true,
    autoAdvance: true
  },
  {
    id: 'merge',
    titleKey: 'demoGuideMergeTitle',
    bodyKey: 'demoGuideMergeBody',
    hintKey: 'demoGuideMergeHint',
    targetId: 'order-panel-form',
    placement: 'left',
    ensureTradeOpen: true,
    focusId: 'order-tab-merge',
    seedUi: true,
    autoAdvance: true
  },
  {
    id: 'resolve',
    titleKey: 'demoGuideResolveTitle',
    bodyKey: 'demoGuideResolveBody',
    hintKey: 'demoGuideResolveHint',
    targetId: 'manage-market-panel',
    fallbackTargetId: 'manage-market-trigger',
    placement: 'bottom',
    focusId: 'manage-market-trigger',
    autoAdvance: true
  },
  {
    id: 'done',
    titleKey: 'demoGuideDoneTitle',
    bodyKey: 'demoGuideDoneBody',
    hintKey: 'demoGuideDoneHint',
    targetId: 'market-detail-header',
    placement: 'bottom'
  }
]

const isWalkthroughNodeVisible = (node) => {
  if (!node || typeof node.getBoundingClientRect !== 'function') return false
  const rect = node.getBoundingClientRect()
  if (rect.width < 2 || rect.height < 2) return false
  const style = window.getComputedStyle?.(node)
  if (!style) return true
  return style.visibility !== 'hidden' && style.display !== 'none' && style.opacity !== '0'
}

export const getDemoWalkthroughStepTargets = (step = {}) =>
  [step.targetId, step.fallbackTargetId].filter(Boolean)

export const queryDemoWalkthroughNodes = (targets = [], { preferTradeSheet = false } = {}) => {
  const list = Array.isArray(targets) ? targets : [targets]
  const nodes = []

  for (const target of list.map((part) => String(part || '').trim()).filter(Boolean)) {
    const selector = target.startsWith('#') || target.startsWith('.') || target.includes('[')
      ? target
      : `#${target}`
    document.querySelectorAll(selector).forEach((node) => {
      if (isWalkthroughNodeVisible(node)) nodes.push(node)
    })
  }

  if (preferTradeSheet) {
    const inSheet = nodes.filter((node) =>
      node.closest?.('#order-panel, .market-detail-trade-sheet, .order-panel--modal')
    )
    if (inSheet.length) return inSheet
  }

  return nodes
}

export const unionDemoWalkthroughRect = (nodes = [], pad = 10) => {
  if (!nodes.length) return null
  let top = Infinity
  let left = Infinity
  let right = -Infinity
  let bottom = -Infinity
  for (const node of nodes) {
    const rect = node.getBoundingClientRect()
    if (rect.width < 2 || rect.height < 2) continue
    top = Math.min(top, rect.top)
    left = Math.min(left, rect.left)
    right = Math.max(right, rect.right)
    bottom = Math.max(bottom, rect.bottom)
  }
  if (!Number.isFinite(top)) return null

  const nextTop = top - pad
  const nextLeft = left - pad
  const nextRight = right + pad
  const nextBottom = bottom + pad

  return {
    top: nextTop,
    left: nextLeft,
    width: Math.max(0, nextRight - nextLeft),
    height: Math.max(0, nextBottom - nextTop),
    bottom: nextBottom,
    right: nextRight
  }
}

/**
 * Build a spotlight rect locked to real element bounds.
 * Prefer the primary target id; never crop/move the hole.
 */
export const pickDemoWalkthroughRect = (nodes = [], pad = 10) => {
  if (!nodes.length) return null
  return unionDemoWalkthroughRect(nodes.slice(0, 1), pad)
}

/** @deprecated use queryDemoWalkthroughNodes + pickDemoWalkthroughRect */
export const resolveDemoWalkthroughTarget = (selectorList = '') =>
  queryDemoWalkthroughNodes(selectorList)[0] ?? null

